import { RefObject, useEffect, useRef, useState } from 'react';

interface Size {
  width: number;
  height: number;
}

interface useResizeObserverProps {
  /**
   * The ref of the element to observe.
   */
  ref: RefObject<HTMLElement | null>;
  /**
   * Callback function that is called when the observed element is resized.
   */
  onResize?: (size: Size) => void;
}

/**
 * A custom hook that observes the resize of a DOM element(border boxSizing)
 * @todo Implementing different boxSizing type support.
 * @returns The current size of the observed element.
 */
export const useResizeObserver = ({ ref, onResize }: useResizeObserverProps): Size => {
  const [{ width, height }, setSize] = useState<Size>({ width: 0, height: 0 });
  const previousSize = useRef<Partial<Size>>({ width: undefined, height: undefined });

  useEffect(() => {
    if (!ref.current) return;

    const observer = new ResizeObserver(([entry]) => {
      const width = entry.borderBoxSize[0].inlineSize;
      const height = entry.borderBoxSize[0].blockSize;
      if (previousSize.current.width !== width || previousSize.current.height !== height) {
        const size = { width, height };
        setSize(size);
        previousSize.current = size;
        onResize?.(size);
      }
    });

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [onResize, ref]);

  return {
    width,
    height
  };
};
